import db from '../config/firebaseConfig';
import { getFirestore, collection, Timestamp, addDoc, getDocs, updateDoc, getDoc, setDoc, deleteDoc, query, where, orderBy, doc, arrayUnion } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';

// 🏢 Client Organization functions

export const createClientOrganizationInFirestore = async (organizationName, userId) => {    
    const docRef = await addDoc(collection(db, "clientOrganizations"), {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        name: organizationName,        
        members: [userId],
    });
    return docRef;
};

export const fetchClientOrganizationsFromFirestore = async (userId) => {
    try {
        const querySnapshot = await getDocs(query(collection(db, "clientOrganizations"), where("members", "array-contains", userId)));
        let organizationsBelongedTo = []; 

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            organizationsBelongedTo.push({ id: doc.id, ...data }); // Append data with document ID
        });

        console.log("FROM FIRESTORE UTILITIES: Organizations belonged to: ", organizationsBelongedTo);
        return organizationsBelongedTo; 
    } catch (error) {
        console.error("Error fetching organizationsBelongedTo: ", error);
        throw error;
    }
};


// 🏡 Program functions

export const createProgramInFirestore = async (programDetails, userId, clientOrganizationId, programName) => {
    const { primaryState, nteeFull, additionalDetails } = programDetails;
    const docRef = await addDoc(collection(db, "programs"), {
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: userId,
        clientOrganizationId: clientOrganizationId,
        programName: programName,
        primaryState: primaryState,
        nteeFull: nteeFull,
        additionalDetails: additionalDetails
    });
    return docRef;
};

export const fetchClientProgramsFromFirestore = async (organizationId) => {
    console.log("Fetching client programs from Firestore")
    try {
        const querySnapshot = await getDocs(query(collection(db, "programs"), where("clientOrganizationId", "==", organizationId)));
        let clientPrograms = []; 

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            clientPrograms.push({ id: doc.id, ...data }); 
        });

        console.log("Client programs fetched: ", clientPrograms)
        return clientPrograms;
    } catch (error) {
        console.error("Error fetching client programs: ", error);
        throw error;
    }
};

export const updateProgramInFirestore = async (programId, programName, primaryState, nteeFull, additionalDetails) => {
    const docRef = doc(db, "programs", programId); // Reference to the document to be updated

    await updateDoc(docRef, {
        updatedAt: Date.now(),
        programName: programName,
        primaryState: primaryState,
        nteeFull: nteeFull,
        additionalDetails: additionalDetails
    });

    return docRef;
};

export const deleteProgramFromFirestore = async (programId) => {        
    try {                                                                
        await deleteDoc(doc(db, "programs", programId));            
        console.log("Program successfully deleted!");
    } catch (error) {
        console.error("Error removing document: ", error);
    }
}

// 🤝 Collaboration functions

export const fetchMemberEmails = async (currentClientOrganization) => {
    const db = getFirestore();
    const emails = [];
    for (const memberId of currentClientOrganization.members) {
        const docRef = doc(collection(db, 'users'), memberId);
        const docSnapshot = await getDoc(docRef);
        if (docSnapshot.exists()) {
            emails.push(docSnapshot.data().email);
        }
    }
    console.log("Member Emails:", emails);
    return emails;
};


export const handleAddTeammateToFirestore = async (currentClientOrganization) => {
    const email = prompt("Enter teammate's email:");
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userId = querySnapshot.docs[0].id;
            const orgRef = doc(db, 'clientOrganizations', currentClientOrganization.id);
            await updateDoc(orgRef, {
                members: arrayUnion(userId)
            });
            alert('Teammate added successfully!');
        } else {
            alert('No user found with that email.');
        }
    } else {
        alert('Please enter a valid email.');
    }
};

// 💰 Membership functions

export const handleAddLicenseKeyClicked = async (clientOrganizationId) => {
    const licenseKey = prompt("Please enter your license key:");
    if (licenseKey) {
        console.log("Checking for purchase with sale ID: ", licenseKey)
        const purchaseRef = collection(db, "purchases"); // Reference to the collection
        const q = query(purchaseRef, where('saleId', '==', licenseKey), where('claimed', '!=', true)); // Query the collection
        const querySnapshot = await getDocs(q); // Get the documents matching the query

        if (!querySnapshot.empty) {
            const purchaseDoc = querySnapshot.docs[0]; // Get the first matching document
            await updateDoc(purchaseDoc.ref, { claimed: true, clientOrganizationId: clientOrganizationId}); // Update the document
            alert("License key claimed successfully! Refreshing page...");
            window.location.reload();
        } else {
            alert("Invalid license key. Please try again.");
        }
    }
};


export const fetchMembershipDetailsFromFirestore = async (userEmail) => {
    try {
        const querySnapshot = await getDocs(query(collection(db, "purchases"), where("email", "==", userEmail)));
        let membershipDetails = null; // Initialize membershipDetails

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            console.log(data);
            membershipDetails = data; // Assign data to membershipDetails
        });

        return membershipDetails; // Return membershipDetails
    } catch (error) {
        console.error("Error fetching membership details: ", error);
        throw error;
    }
};

export const fetchMembershipDetailsFromFirestoreForOrganization = async (clientOrganizationId) => {
    try {
        const querySnapshot = await getDocs(query(collection(db, "purchases"), where("clientOrganizationId", "==", clientOrganizationId)));
        let membershipDetails = null; // Initialize membershipDetails

        querySnapshot.forEach((doc) => {
            const data = doc.data();
            // console.log(data);
            membershipDetails = data; // Assign data to membershipDetails
        });

        console.log("MEMBER: ", membershipDetails)

        return membershipDetails; // Return membershipDetails
    } catch (error) {
        console.error("Error fetching membership details: ", error);
        throw error;
    }
};


// 📝 Writing functions

export const createBlankDocumentInFirestore = async  (proposalId, documentName) => {
    const docRef = await addDoc(collection(db, "grantDocuments"), {
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),  
        content: '',
        documentName: documentName,        
        proposalId: proposalId,
    });
    return docRef.id;
};

export const updateGrantDocumentNameInFirestore = async (documentId, newName) => {
    const docRef = doc(db, "grantDocuments", documentId);
    await updateDoc(docRef, {
        documentName: newName
    });
}

export const refactoredCreateGrantDocumentInFirestore = async (proposalId, documentName, content) => {
    const docRef = await addDoc(collection(db, "grantDocuments"), {
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),  
        content: content,
        proposalId: proposalId,
        documentName: documentName,
    });
    return docRef.id;
}

export const deleteGrantDocumentFromFirestore = async (documentId) => {
    try {                                                                
        await deleteDoc(doc(db, "grantDocuments", documentId));            
        console.log("Document successfully deleted!");
    } catch (error) {
        console.error("Error removing document: ", error);
    }
};

// This updates the text content of the document
export const updateGrantDocumentContentInFirestore = async (grantDocumentId, content) => {
    const docRef = doc(db, 'grantDocuments', grantDocumentId); // Adjust the collection name as needed
    try {
        await updateDoc(docRef, {
            content: content,
            updatedAt: Timestamp.now() // Update the content field
        });
        console.log("Document successfully updated!");
    } catch (error) {
        console.error("Error updating document: ", error);
    }
};

export const fetchGrantDocumentsFromFirestoreForProposalId = async (proposalId) => {
    
    try {
        console.log("Fetching grant documents for proposal ID: ", proposalId);
        const q = query(collection(db, "grantDocuments"), where("proposalId", "==", proposalId), orderBy("updatedAt", "desc"));
        const querySnapshot = await getDocs(q);
        const docsArray = [];
        querySnapshot.forEach(doc => {
            docsArray.push({ id: doc.id, ...doc.data() });
        });
        return docsArray; 
    } catch (error) {
        console.error("Error fetching grant documents:", error);
        return [];
    }
};

export const fetchSingleGrantDocumentFromFirestore = async (grantDocumentId) => {
    
    try {
        console.log("Fetching single grant document from Firestore", grantDocumentId);
        const docRef = doc(db, "grantDocuments", grantDocumentId); // Use documentId as the reference
        const docSnapshot = await getDoc(docRef); // Fetch the document snapshot

        if (docSnapshot.exists()) {
            return { id: docSnapshot.id, ...docSnapshot.data() }; // Return the document data
        } else {
            console.log("No such document!");
            return null; // Return null if the document does not exist
        }
    } catch (error) {
        console.error("Error fetching grant documents:", error);
        return null; // Return null in case of an error
    }
};

export const createGrantDocumentBackupInFirestore = async (grantDocumentId, content) => {
    const backupDocRef = doc(db, 'grantDocumentBackups', uuidv4());
    try {
        await setDoc(backupDocRef, {
            grantDocumentId: grantDocumentId,
            content: content,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now() // Update the content field
        });
        console.log("Document successfully updated!");
    } catch (error) {
        console.error("Error updating document: ", error);
    }
};

// 💰 Proposal / Opportunity functions

export const updatedLinkedFunderEinForProposalInFirestore = async (proposalId, funderEin) => {
    const docRef = doc(db, "savedOpportunities", proposalId);
    await updateDoc(docRef, {
        linkedFunderEin: funderEin
    });
}

export const deleteSavedOpportunityInFirestore = async (opportunityId) => {
    try {
        console.log(`Attempting to delete opportunity with ID: ${opportunityId}`);
        await deleteDoc(doc(db, "savedOpportunities", opportunityId));
        console.log("Opportunity successfully deleted!");
    } catch (error) {
        console.error("Error removing document: ", error);
    }
}

export const fetchOpportunitiesFromFirestoreForProgramId = async (grantSeekingProgramId) => {
    const q = query(collection(db, "savedOpportunities"), where("grantSeekingProgramId", "==", grantSeekingProgramId));
    const querySnapshot = await getDocs(q);
    const opportunities = [];
    querySnapshot.forEach(doc => {
        opportunities.push({ id: doc.id, ...doc.data() });
    });
    return opportunities;
};

export const refactoredSaveOpportunityToFirestore = async ({ 
    grantSeekingProgramId,
    year, 
    opportunityName, 
    linkedFunderEin,
    amount, 
    status, 
    notes, 
    deadline, 
    website,
}) => {
    try {
        // Check if required fields are set
        if (!grantSeekingProgramId || !year || !opportunityName) {
            console.error("Missing required fields: grantSeekingProgramId, year, or opportunityName");
            return;
        }
        
        const opportunity = {
            grantSeekingProgramId: grantSeekingProgramId,
            year: year,
            name: opportunityName,
            linkedFunderEin: linkedFunderEin || null,
            amount: amount || null,
            status: status || null,
            notes: notes || null,
            deadline: deadline || null,
            website: website || null,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        };

        const docRef = await addDoc(collection(db, "savedOpportunities"), opportunity);

        console.log("Opportunity saved successfully with ID: ", docRef.id);
    } catch (error) {
        console.error("Error saving opportunity: ", error);
    }
};

export const refactoredUpdateOpportunityToFirestore = async ({
    opportunityId,
    grantSeekingProgramId,
    year, 
    opportunityName, 
    linkedFunderEin,
    amount, 
    status, 
    notes, 
    deadline, 
    website,
}) => {
    try {
        // Check if opportunityId is provided
        if (!opportunityId) {
            console.error("Missing required field: opportunityId");
            return;
        }

        // Check if required fields are set
        if (!grantSeekingProgramId || !year || !opportunityName) {
            console.error("Missing required fields: grantSeekingProgramId, year, or opportunityName");
            return;
        }

        const docRef = doc(db, "savedOpportunities", opportunityId);
        
        const opportunity = {
            grantSeekingProgramId: grantSeekingProgramId,
            year: year,
            name: opportunityName,
            linkedFunderEin: linkedFunderEin || null,
            amount: amount || null,
            status: status || null,
            notes: notes || null,
            deadline: deadline || null,
            website: website || null,
            updatedAt: Timestamp.now(),
        };

        await updateDoc(docRef, opportunity);
        console.log("Opportunity updated successfully with ID: ", opportunityId);
    } catch (error) {
        console.error("Error updating opportunity: ", error);
        throw error; // Rethrow the error for handling in the calling function
    }
};

export const updateOpportunityNameInFirestore = async (opportunityId, newName) => {
    const docRef = doc(db, 'savedOpportunities', opportunityId);
    await updateDoc(docRef, {
        name: newName,
        updatedAt: Timestamp.now()
    });
};


// ✅ Task functions

export const createTaskInFirestore = async (task) => {
    try {
        const docRef = await addDoc(collection(db, "tasks"), task);
        console.log("Task created successfully with ID: ", docRef.id);
    } catch (error) {
        console.error("Error creating task: ", error);
    }
};

export const updateTaskInFirestore = async (taskId, task) => {
    const docRef = doc(db, 'tasks', taskId);
    await updateDoc(docRef, task);
    console.log("Task updated successfully!");
};

export const deleteTaskInFirestore = async (taskId) => {
    console.log("Deleting task in Firestore: ", taskId);
    await deleteDoc(doc(db, "tasks", taskId));            
    console.log("Task successfully deleted!");
};

export const fetchTasksFromFirestoreForProposalId = async (proposalId) => {
    console.log("Fetching tasks for proposal ID: ", proposalId);
    const q = query(collection(db, "tasks"), where("proposalId", "==", proposalId));
    const querySnapshot = await getDocs(q);
    const tasks = [];
    querySnapshot.forEach(doc => {
        tasks.push({ id: doc.id, ...doc.data() });
    });
    return tasks;
};
